import React, { useContext, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import JSXStyle from 'styled-jsx/style';

import styles from './ResponsiveMode.desktop.module.css';
import { AppContext } from '../../../services/AppContext';
import {
	ResponsiveModeContext,
	formatTime,
	useInternalLink,
	useScrollToHash,
	useArticleSearchResult,
} from './ResponsiveModeContext';
import imgLogo from '../../../components/navbar/logo.png';
import { Icon } from '../../../components/Icon';
import { Article } from '../../../components/canvasflow/article/Article';
import useDebounce from '../../../hooks/use-debounce';

import { Carousel } from './Carousel';

export const ResponsiveDesktopContext = React.createContext({
	togglePlayer: null,
	isPlayerOpen: false,
	setIsPlayerOpen: null,
});

export const ResponsiveModeDesktop = () => {
	let { setIsFontOpen, swiper, player } = useContext(ResponsiveModeContext);
	const { isPrinting, isIframeMode, theme, config } = useContext(AppContext);
	const { fontSizeOption } = config;
	const [isTocOpen, setIsTocOpen] = useState(false);
	const [isShareOpen, setIsShareOpen] = useState(false);
	const [isPlayerOpen, setIsPlayerOpen] = useState(false);
	let className = ['App', styles['issue']];
	if (!isTocOpen) {
		className.push(styles['toc-close']);
	}

	useEffect(() => {
		if (swiper) {
			try {
				swiper.update();
				swiper.init();
			} catch (e) {
				console.error(e);
			}
		}
	}, [isTocOpen]);

	if (isPrinting) {
		className.push(styles['print-view'], 'print-view');
		return <PrintView />;
	}

	let navbar = (
		<Navbar
			isTocOpen={isTocOpen}
			toggleShare={() => {
				if (!isShareOpen) {
					setIsFontOpen(false);
				}
				setIsShareOpen(!isShareOpen);
			}}
			setIsShareOpen={setIsShareOpen}
			toggleToc={() => {
				setIsTocOpen(!isTocOpen);
			}}
		/>
	);

	let progressBar = <ProgressBar />;
	let fontSlider = <FontSlider />;
	let toc = <TOC />;
	let share = <Share isOpen={isShareOpen} />;

	if (isPrinting) {
		navbar = null;
		progressBar = null;
		fontSlider = null;
		toc = null;
		share = null;
	}

	if (isIframeMode && config.iframe.enabled && !config.iframe.toolbars) {
		className = ['App', styles['issue'], styles['iframe-view']];
	}

	if (fontSizeOption?.enabled === false) {
		fontSlider = null;
	}

	const height = `calc(100% - ${
		theme?.progressBar?.height ? theme?.progressBar?.height : 5
	}px - ${theme?.navbar?.height ? theme?.navbar?.height : 50}px)`;

	return (
		<ResponsiveDesktopContext.Provider
			value={{
				isPlayerOpen: !!(isPlayerOpen && player),
				setIsPlayerOpen: setIsPlayerOpen,
				togglePlayer: () => setIsPlayerOpen(!isPlayerOpen),
			}}
		>
			<div className={className.join(' ')}>
				{navbar}
				{progressBar}
				{fontSlider}
				{share}
				<section
					className={styles['container']}
					style={{
						minHeight: height,
						height,
					}}
				>
					{toc}
					<Content isTocOpen={isTocOpen} />
				</section>
			</div>
		</ResponsiveDesktopContext.Provider>
	);
};

const PrintView = () => {
	const { styles } = useContext(AppContext);
	const { article, lang } = useContext(ResponsiveModeContext);
	const { id, style, components } = article;

	return (
		<div className={'print-view'}>
			<div>
				<Article
					id={id}
					index={0}
					style={`${style}`}
					fontSize={45}
					styles={styles}
					lang={lang}
					components={components}
				/>
			</div>
		</div>
	);
};

const Navbar = ({ isTocOpen, toggleToc, toggleShare, setIsShareOpen }) => {
	const {
		onFontClick,
		onIsFavoriteClick,
		lang,
		isFavorite,
		isOnline,
		isFontOpen,
		article,
		player,
		issue,
		isImageModeCapable,
		onImageModeClick,
	} = useContext(ResponsiveModeContext);

	const { togglePlayer } = useContext(ResponsiveDesktopContext);
	const { config, theme, tts, locale } = useContext(AppContext);

	const { fontSizeOption } = config;

	let bookmarkButton = null;
	let externalButton = null;
	let replicaModeBtn = null;
	const { background, color, height } = theme.navbar;

	const isPlayerEnabled = !!player;

	if (isImageModeCapable && config?.contentType === 'responsive-replica') {
		replicaModeBtn = (
			<button onClick={onImageModeClick}>
				<Icon name="replica-mode" color={color} />
			</button>
		);
	}

	if (navigator.share) {
		toggleShare = () => {
			const share = {
				title: article?.name?.[lang] || '',
				text: article?.description || '',
				url: window.location.href,
			};
			navigator.share(share);
		};
	}

	if (config?.bookmarks?.enabled) {
		const bookmarkLabel = isFavorite
			? 'Unbookmark article'
			: 'Bookmark article';
		bookmarkButton = (
			<button
				onClick={() => {
					onIsFavoriteClick();
					if (!isFavorite) {
						setTimeout(() => {
							alert('Article bookmarked');
						}, 200);
					}
				}}
				aria-label={bookmarkLabel}
				title={bookmarkLabel}
			>
				<Icon
					name={isFavorite ? 'bookmark-selected' : 'bookmark'}
					color={color}
				/>
			</button>
		);
		bookmarkButton = null;
	}

	if (issue?.externalUrl) {
		externalButton = (
			<button
				onClick={() => {
					window.open(issue?.externalUrl, '_blank');
				}}
				aria-label={locale['BUTTON_TITLE_EXTERNAL_URL']}
				title={locale['BUTTON_TITLE_EXTERNAL_URL']}
			>
				<Icon name={'external-link'} color={color} />
			</button>
		);
	}

	return (
		<nav
			className={styles['navbar']}
			style={{ background, height, minHeight: height, maxHeight: height }}
		>
			<div style={{ maxHeight: height }} className={styles['logo']}>
				<Link to={`/`}>
					<img
						src={imgLogo}
						style={{ maxHeight: height }}
						alt="logo"
					/>
				</Link>
			</div>
			<div style={{ maxHeight: height }}>
				{!isOnline ? (
					<div className={styles['offline']}>
						<Icon name={'cloud-off'} color={color} />
					</div>
				) : (
					<div className={styles['offline']}>
						<Icon name={'cloud-off'} color={color} opacity={0} />
					</div>
				)}

				<button
					onClick={toggleToc}
					aria-label={'Table of contents'}
					title={'Table of contents'}
				>
					<Icon
						name={isTocOpen ? 'horizontal-split' : 'toc'}
						color={color}
					/>
				</button>
				<LanguageButton article={article} lang={lang} />
				{replicaModeBtn}
				{bookmarkButton}

				{fontSizeOption?.enabled ? (
					<button
						onClick={() => {
							if (!isFontOpen) {
								setIsShareOpen(false);
							}
							onFontClick();
						}}
						title={'Font Size'}
						aria-label={'Font Size'}
					>
						<Icon name={'font'} color={color} />
					</button>
				) : null}

				{tts?.enabled ? (
					<button
						onClick={togglePlayer}
						title={'Player'}
						disabled={!isPlayerEnabled}
						aria-label={'Player'}
					>
						<Icon name={'player'} color={color} />
					</button>
				) : null}
				{externalButton}
				{config?.share?.enabled ? (
					<button
						className={styles['share-button']}
						onClick={toggleShare}
						title={'Share'}
						aria-label={'Share'}
					>
						<Icon name={'share'} color={color} />
					</button>
				) : null}

				<SearchBar />
			</div>
		</nav>
	);
};

Navbar.propTypes = {
	isTocOpen: PropTypes.bool,
	toggleToc: PropTypes.func,
	toggleShare: PropTypes.func,
	setIsShareOpen: PropTypes.func,
};

function LanguageButton({ article, lang }) {
	const { onLanguageSelected } = useContext(ResponsiveModeContext);
	const { onLangChange } = useContext(AppContext);

	const languages = Object.keys(article.name);
	if (languages.length <= 1) {
		return null;
	}

	return (
		<select
			onChange={(e) => {
				console.log(`The selected language is: ${e.target.value}`);
				onLanguageSelected(e.target.value);
				onLangChange(e.target.value);
			}}
			value={lang}
		>
			{languages.map((language) => (
				<option key={language} value={language}>
					{language}
				</option>
			))}
		</select>
	);
}

LanguageButton.propTypes = {
	article: PropTypes.any,
	lang: PropTypes.string,
};

const SearchBar = () => {
	const { onSearch } = useContext(ResponsiveModeContext);
	const { theme } = useContext(AppContext);
	const [isExpanded, setIsExpanded] = useState(false);
	const [searchTerm, setSearchTerm] = useState('');
	const debouncedSearchTerm = useDebounce(searchTerm, 500);
	const { color } = theme.navbar;
	useEffect(() => {
		onSearch(debouncedSearchTerm);
	}, [debouncedSearchTerm]);
	const className = [styles['search-bar']];
	if (isExpanded) {
		className.push(styles['expanded']);
	}
	return (
		<div className={className.join(' ')}>
			{isExpanded ? (
				<span className={styles['icon']}>
					<Icon name={'search-alt'} color={'#c4c4c4'} />
				</span>
			) : (
				<button
					aria-label={'Search'}
					title={'Search'}
					className={styles['icon']}
					onClick={() => setIsExpanded(true)}
				>
					<Icon name={'search-alt'} color={color} />
				</button>
			)}
			{isExpanded ? (
				<input
					type={'text'}
					value={searchTerm}
					onChange={(e) => {
						e.preventDefault();
						setSearchTerm(e.target.value);
					}}
				/>
			) : null}
			{isExpanded ? (
				<button
					title={'Clear'}
					aria-label={'Clear'}
					className={[styles['icon'], styles['close']].join(' ')}
					onClick={() => {
						onSearch('');
						setSearchTerm('');
						setIsExpanded(false);
					}}
				>
					<Icon name={'cancel'} color={'#c4c4c4'} />
				</button>
			) : null}
		</div>
	);
};

const Loading = ({ color }) => {
	return (
		<button className={styles['loading']}>
			<svg x="0px" y="0px" viewBox="0 0 100 100">
				<circle fill={color} stroke="none" cx="25" cy="50" r="6">
					<animate
						attributeName="opacity"
						dur="1s"
						values="0;1;0"
						repeatCount="indefinite"
						begin="0.1"
					/>
				</circle>
				<circle fill={color} stroke="none" cx="50" cy="50" r="6">
					<animate
						attributeName="opacity"
						dur="1s"
						values="0;1;0"
						repeatCount="indefinite"
						begin="0.2"
					/>
				</circle>
				<circle fill={color} stroke="none" cx="75" cy="50" r="6">
					<animate
						attributeName="opacity"
						dur="1s"
						values="0;1;0"
						repeatCount="indefinite"
						begin="0.3"
					/>
				</circle>
			</svg>
		</button>
	);
};

Loading.propTypes = {
	color: PropTypes.string,
};

const ProgressBar = () => {
	const { theme } = useContext(AppContext);
	const { index, articles } = useContext(ResponsiveModeContext);
	const percentage = ((index + 1) * 100) / articles.length;
	if (!theme || !theme.progressBar || !theme.progressBar.enabled) {
		return <div className={styles['progress-bar']} />;
	}
	let { height, background, color } = theme.progressBar;
	return (
		<div
			className={styles['progress-bar']}
			style={{
				background: background ? background : '#fff',
				minHeight: height ? `${height}px` : 5,
				height: height ? `${height}px` : 5,
			}}
		>
			<div
				style={{
					width: `${percentage}%`,
					background: color ? color : '#000',
				}}
			/>
		</div>
	);
};

function TOC() {
	const { article, articles } = useContext(ResponsiveModeContext);
	const { theme } = useContext(AppContext);
	const background = theme?.toc?.background || '#fff';
	const listStyleType = theme?.toc?.listStyle || 'none';
	return (
		<aside className={styles['toc']}>
			<ol
				className={styles['toc']}
				style={{
					background,
					listStyleType,
				}}
			>
				{articles
					.map((art, i) => {
						art.index = i;
						return art;
					})
					.filter((article) => article?.channel?.toc !== 'excluded')
					.map((art, i) => (
						<TOCItem
							key={i}
							index={i}
							article={art}
							selected={art?.id === article?.id}
						/>
					))}
			</ol>
		</aside>
	);
}

function TOCItem({ article, selected, index }) {
	const classNames = [styles['item']];

	if (selected) {
		classNames.push(styles['selected']);
	}
	const { lang, onTocItemClick, clearNavigationStack } = useContext(
		ResponsiveModeContext
	);
	const { theme, config, getReadTime } = useContext(AppContext);

	const { readTime } = config;

	const textTransform = theme?.toc?.textTransform || undefined;
	const fontFamily = theme?.toc?.fontFamily || undefined;
	const selectedFontFamily = theme?.toc?.selectedFontFamily || undefined;
	const style = {
		overflow: 'hidden',
		fontFamily: selected ? selectedFontFamily : fontFamily,
		textTransform,
	};
	const { name, components, description } = article;

	let componentCSS = `
		
	`;

	const isThumbnailsEnabled = !!theme?.toc?.thumbnails?.enabled;
	if (isThumbnailsEnabled) {
		style.display = 'grid';
	} else {
		style.display = 'flex';
		style.padding = 18;
	}

	let readTimeText = '';
	if (readTime?.enabled) {
		let { minutes, seconds } = getReadTime(components, lang);
		if (minutes < 1) {
			readTimeText = ' 1 min';
		} else {
			if (seconds > 30) {
				minutes += 1;
			}

			readTimeText = minutes === 1 ? ` 1 min` : ` ${minutes} min`;
		}
	}

	return (
		<li
			id={`toc-item-${index}`}
			className={classNames.join(' ')}
			style={style}
			onClick={() => {
				clearNavigationStack();
				onTocItemClick(article);
			}}
		>
			<div className={styles['item-content']}>
				<span>{name[lang]}</span>
				{description ? <p>{description}</p> : null}
				{!readTimeText ? null : <small>{readTimeText} read</small>}
			</div>
			<JSXStyle id={`toc-${index}`}>{componentCSS}</JSXStyle>
		</li>
	);
}

TOCItem.propTypes = {
	article: PropTypes.object.isRequired,
	selected: PropTypes.bool.isRequired,
	index: PropTypes.number,
};

const Content = ({ isTocOpen }) => {
	const { isPrinting } = useContext(AppContext);
	useScrollToHash();
	const { contentRef } = useInternalLink();
	return (
		<main ref={contentRef} className={styles['content']}>
			<Carousel />
			{isPrinting || isTocOpen ? null : (
				<NavigationArrow direction={'left'} />
			)}
			{isPrinting || isTocOpen ? null : (
				<NavigationArrow direction={'right'} />
			)}
			{isPrinting ? null : <SearchResults />}
			<PlayerToolbar />
			<NavigationBackButton />
		</main>
	);
};

Content.propTypes = {
	isTocOpen: PropTypes.bool.isRequired,
};

function NavigationBackButton() {
	const { navigationStack, popNavigationStack } = useContext(
		ResponsiveModeContext
	);
	if (!navigationStack || !navigationStack.length) {
		return null;
	}
	return (
		<button
			className={styles['navigation-stack-btn']}
			onClick={() => {
				popNavigationStack();
			}}
		>
			<Icon name={'skip-back'} color={'#000'} />
		</button>
	);
}

const SearchResults = () => {
	const content = useSearchResults();
	const { results } = useContext(ResponsiveModeContext);
	if (!results) {
		return null;
	}
	const className = [styles['search-results']];
	if (!results.length) {
		className.push(styles['empty']);
	}
	return <div className={className.join(' ')}>{content}</div>;
};

function useSearchResults() {
	const [content, setContent] = useState(null);
	const { results } = useContext(ResponsiveModeContext);
	useEffect(() => {
		if (!results) {
			setContent(null);
			return;
		}

		if (!results.length) {
			setContent(<small>No articles where found</small>);

			return;
		}

		setTimeout(() => {
			setContent(
				<ul>
					{results.map((article, i) => (
						<SearchArticleResultItem
							key={article.id}
							article={article}
							index={i}
						/>
					))}
				</ul>
			);
		}, 500);
	}, [results]);
	return content;
}

const SearchArticleResultItem = ({ article, index }) => {
	const [isCollapse, setIsCollapse] = useState(index !== 0);
	const { lang, search, onTocItemClick, navigateToResult } = useContext(
		ResponsiveModeContext
	);
	const { theme } = useContext(AppContext);
	const { id, name } = article;

	const isThumbnailsEnabled = !!theme?.toc?.thumbnails?.enabled;

	// console.log(`Article found: ${article.name[lang]}`, article);

	const onToggleClick = () => {
		setIsCollapse(!isCollapse);
	};

	const classNames = [styles['article-item']];
	if (!isCollapse) {
		classNames.push(styles['open']);
	}

	return (
		<li
			id={`search-result-${id}`}
			className={classNames.join(' ')}
			style={{
				display: isThumbnailsEnabled ? 'grid' : 'flex',
				padding: isThumbnailsEnabled ? null : 18,
			}}
		>
			<div className={styles['description']}>
				<h4
					onClick={() => {
						onTocItemClick(article);
						navigateToResult(article);
					}}
				>
					{name[lang]}
				</h4>
				<div>
					<button onClick={onToggleClick}>
						<Icon
							name={isCollapse ? 'arrow-right' : 'arrow-down'}
							color={'#104a89'}
						/>
					</button>
				</div>
			</div>
			<SearchResultList
				search={search}
				article={article}
				lang={lang}
				isCollapse={isCollapse}
			/>
		</li>
	);
};

SearchArticleResultItem.propTypes = {
	article: PropTypes.object.isRequired,
	index: PropTypes.number,
};

const SearchResultList = ({ isCollapse, article, lang, search }) => {
	const components = useArticleSearchResult(article);
	const classNames = [styles['result-list']];
	if (isCollapse) {
		classNames.push(styles['collapse']);
	}

	const sentences = components.reduce((acc, component) => {
		const { text_lang } = component;
		if (!text_lang) {
			return acc;
		}
		if (!text_lang[lang]) {
			return acc;
		}
		const sentences = text_lang[lang]
			.replace(/<[^>]*>/g, '')
			.split('.')
			.filter((sentence) => new RegExp(search, 'gmi').test(sentence))
			.map((sentence) =>
				sentence.replace(
					new RegExp(search, 'gmi'),
					(match) => `<mark>${match}</mark>`
				)
			);
		for (const sentence of sentences) {
			acc.push({
				id: component.id,
				text: sentence,
				article,
			});
		}
		return acc;
	}, []);
	return (
		<ul className={classNames.join(' ')}>
			{sentences.map((sentence, i) => {
				return (
					<SearchResultItem
						key={`${sentence.id}-${i}`}
						sentence={sentence}
					/>
				);
			})}
		</ul>
	);
};

SearchResultList.propTypes = {
	isCollapse: PropTypes.bool,
	article: PropTypes.object.isRequired,
	lang: PropTypes.string,
	search: PropTypes.string,
};

const SearchResultItem = ({ sentence }) => {
	const { onTocItemClick, navigateToResult } = useContext(
		ResponsiveModeContext
	);
	return (
		<li
			onClick={() => {
				console.log(sentence);
				onTocItemClick(sentence.article);
				navigateToResult(sentence.article, sentence.id);
			}}
		>
			<p
				dangerouslySetInnerHTML={{
					__html: sentence.text,
				}}
			></p>
		</li>
	);
};

SearchResultItem.propTypes = {
	sentence: PropTypes.string.isRequired,
};

const Share = ({ isOpen }) => {
	const { config } = useContext(AppContext);
	if (!config?.share?.enabled) {
		return null;
	}
	const { article, lang } = useContext(ResponsiveModeContext);
	const title = article.name[lang];
	const className = [styles['share-container']];
	if (!isOpen) {
		className.push(styles['hidden']);
	}

	let onShareClick = () => {
		const el = document.createElement('textarea');
		el.value = window.location.href;
		document.body.appendChild(el);
		el.select();
		document.execCommand('copy');
		document.body.removeChild(el);
		alert('Copy url to the clipboard');
	};

	const onEmailClick = () => {
		let body = [title, ' ', window.location.href].join(' %0D%0A ');
		const url = `mailto:?to=&body=${body},&subject=${title}`;
		// window.location.href = url;
		window.open(url);
	};

	return (
		<div className={className.join(' ')}>
			<button
				onClick={onShareClick}
				title={'Copy Link'}
				aria-label={'Copy Link'}
			>
				<Icon name={'add-link'} />
				<h4>Copy Link</h4>
			</button>
			<button
				onClick={onEmailClick}
				title={'Attach to email'}
				aria-label={'Attach to email'}
			>
				<Icon name={'attach-email'} />
				<h4>Email</h4>
			</button>
		</div>
	);
};

Share.propTypes = {
	isOpen: PropTypes.bool,
};

const FontSlider = () => {
	let { fontSize, onFontSizeChange, isFontOpen, theme } = useContext(
		ResponsiveModeContext
	);
	const step = 5;
	const lowerLimit = 50;
	const higherLimit = 100;
	// const color = `#3d3d3d`;

	const className = [styles['font-slider']];
	if (!isFontOpen) {
		className.push(styles['hidden']);
	}

	useEffect(() => {
		onFontSizeChange(fontSize);
	}, [fontSize]);

	const onChange = (direction) => {
		if (direction === 'increase') {
			fontSize = fontSize + step;
			if (fontSize >= higherLimit) {
				fontSize = higherLimit;
			}
		} else {
			fontSize = fontSize - step;
			if (fontSize <= lowerLimit) {
				fontSize = lowerLimit;
			}
		}
		onFontSizeChange(fontSize);
	};

	/*const percentage = Math.round(
		((fontSize - lowerLimit) / (higherLimit - lowerLimit)) * 100
	);*/
	// const background = `linear-gradient(to right, ${color} ${percentage}%, ${color} ${percentage}%, rgba(0,0,0,0.3) ${percentage}%, rgba(0,0,0,0.3) 100%)`;
	return (
		<div
			className={className.join(' ')}
			style={{
				height: `${
					theme?.navbar?.height ? theme?.navbar?.height : 50
				}px`,
			}}
		>
			<div>
				<button
					disabled={fontSize <= lowerLimit}
					title={'Decrease font size'}
					aria-label={'Decrease font size'}
					onClick={() => onChange('decrease')}
				>
					<Icon name={'font-decrease'} />
				</button>
			</div>

			<div>
				<button
					disabled={fontSize >= higherLimit}
					title={'Increase font size'}
					aria-label={'Increase font size'}
					onClick={() => onChange('increase')}
				>
					<Icon name={'font-increase'} />
				</button>
			</div>
		</div>
	);
};

function PlayerToolbar() {
	const { theme } = useContext(AppContext);
	const { article, player } = useContext(ResponsiveModeContext);
	const { isPlayerOpen } = useContext(ResponsiveDesktopContext);
	const [playerState, setPlayerState] = useState(player?.getState());
	const [duration, setDuration] = useState(player?.getState());
	const [position, setPosition] = useState(null);
	const onPlay = (position) => {
		setPosition(Math.floor(position));
	};

	const style = {};

	const isHidden = !isPlayerOpen || !article || !player;
	let { height } = theme.progressBar;
	const progressBarHeight = height ? `${height}px` : '5px';
	useEffect(() => {
		setPlayerState(player?.getState());
		setDuration(Math.floor(player?.getDuration()));
		if (!isPlayerOpen && playerState === 'playing' && player) {
			setPlayerState('stop');
			player.stop();
		}
		if (player) {
			player.onChange('position', onPlay);
		}
	}, [player, article, isPlayerOpen]);
	let onPlayPauseClick = () => {
		setPlayerState('playing');
		player.play();
	};
	if (playerState === 'playing') {
		onPlayPauseClick = () => {
			setPlayerState('pause');
			player.pause();
		};
	}
	const onReplayClick = () => {
		player.rewind(5);
	};
	const onForwardClick = () => {
		player.forward(5);
	};
	const className = [styles['player-toolbar']];
	if (isHidden) {
		className.push(styles['hidden']);
		style.marginTop = `calc(-1 * var(--bottom-bar-height) - ${progressBarHeight} - 2px)`;
	} else {
		className.push(styles['show']);
	}
	return (
		<div className={className.join(' ')} style={style}>
			<div className={styles['controls']}>
				<button
					title={'Backwards 5 seconds'}
					aria-label={'Backwards 5 seconds'}
					onClick={onReplayClick}
					disabled={playerState !== 'playing'}
				>
					<Icon name={'replay-5'} />
				</button>
				<button
					title={'Play / Pause'}
					aria-label={'Play / Pause'}
					onClick={onPlayPauseClick}
				>
					<Icon
						name={
							playerState === 'playing' ? 'pause' : 'play-arrow'
						}
					/>
				</button>
				<button
					title={'Forward 5 seconds'}
					aria-label={'Forward 5 seconds'}
					onClick={onForwardClick}
					disabled={playerState !== 'playing'}
				>
					<Icon name={'forward-5'} />
				</button>
			</div>
			<div className={styles['progress']}>
				{duration
					? `${formatTime(position)} / ${formatTime(duration)}`
					: null}
			</div>
		</div>
	);
}

const NavigationArrow = ({ direction }) => {
	let {
		device: { isTouchable },
		navArrows,
	} = useContext(AppContext);
	const {
		index,
		articles,
		onArticleNextClick,
		onArticlePreviousClick,
		isLightboxMode,
		clearNavigationStack,
	} = useContext(ResponsiveModeContext);

	if (!navArrows?.enabled) {
		return null;
	}
	if (navArrows?.mode === 'always') {
		isTouchable = false;
	}
	const className = [styles['navigation-arrow'], styles[direction]];
	if (isTouchable) {
		return null;
	}
	if (isLightboxMode) {
		return null;
	}
	if (index === 0 && direction === 'left') {
		return null;
	}
	if (index === articles.length - 1 && direction === 'right') {
		return null;
	}
	let onClick =
		direction === 'right' ? onArticleNextClick : onArticlePreviousClick;
	const arrowLabel =
		direction === 'left' ? 'Previous Article' : 'Next Article';
	return (
		<div className={className.join(' ')}>
			<button
				onClick={() => {
					clearNavigationStack();
					onClick();
				}}
				title={arrowLabel}
				aria-label={arrowLabel}
			>
				<Icon name={`chevron-${direction}`} color={'#000'} />
			</button>
		</div>
	);
};

NavigationArrow.propTypes = {
	direction: PropTypes.oneOf(['left', 'right']),
};
